import { ChangeEvent, useState } from 'react'
import { To } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { useDocumentsDataTable } from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import ActionsDatatable from 'modules/documents/components/ActionsDatatable'
import BulkActionsDatatable from 'modules/documents/components/BulkActionsDatatable'
import {
  createdAtFilter,
  visibleFilter,
} from 'modules/documents/components/CompanyDocumentsDatatable/filters'
import ContextFromPage from 'modules/documents/components/DatatableContextFromPage/ContextFromPage'
import DocumentsDataTableEmptyState from 'modules/documents/components/DocumentDatatableEmptyState'
import DraggableDatatable from 'modules/documents/components/DraggableDatatable'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { SPACE } from 'modules/documents/lib/constants'
import {
  actionsProjection,
  authorProjection,
  contentTypeProjection,
  createdAtProjection,
  filenameProjection,
} from 'modules/documents/lib/datatable/documents/projections'

const ITEMS_PER_PAGE = 100

type Props = {
  onNavigationClick: (documentId: number) => void
  onDrop: (files: File[]) => void
  folder?: Folder | null
  initialFolder?: Folder | null
  parentRoute?: To
  multiple?: boolean
  readonly: boolean
  space: SPACE
  onUpload?: () => void
  deleteFeatureEnabled: boolean
}

const CompanyDocumentsDataTable = ({
  folder = null,
  initialFolder,
  parentRoute,
  onDrop,
  multiple = true,
  readonly,
  deleteFeatureEnabled,
  onNavigationClick,
  space,
  onUpload,
}: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [isFiltering, setIsFiltering] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const source = useDocumentsDataTable(
    [
      contentTypeProjection(),
      filenameProjection(),
      authorProjection(),
      createdAtProjection(),
      actionsProjection({
        render: (document) => (
          <ActionsDatatable
            document={document}
            onNavigationClick={onNavigationClick}
            space={space}
            deleteFeatureEnabled={deleteFeatureEnabled}
          />
        ),
      }),
    ],
    {
      variables: {
        space: space,
        search: debouncedSearch,
        folderId: folder?.id,
        byWithoutFolder: folder === null,
      },
    }
  )

  const onDatatableSearch = (value: ChangeEvent<HTMLInputElement>) => {
    onSearch(value)

    if (value.target.value !== '') setIsFiltering(true)
    else {
      setTimeout(() => {
        setIsFiltering(false)
      }, 500)
    }
  }

  const onDragging = (value: boolean) => {
    setIsDragging(value)
  }

  const filters = [createdAtFilter(source), visibleFilter(source)]
  const datatable = (
    <DataTable.Provider
      source={source}
      filters={filters}
      enableClearFilters
      pagination={{
        kind: 'offset',
        itemsPerPage: ITEMS_PER_PAGE,
      }}
      sort={{
        path: 'createdAt',
        direction: 'desc',
      }}
    >
      <Box paddingY="s8" gap="s16">
        <DataTable.HeadBar
          search={{
            value: search,
            onChange: onDatatableSearch,
            placeholder: i18n.t('common.search'),
          }}
          actions={
            !readonly && (
              <BulkActionsDatatable space={space} deleteFeatureEnabled={deleteFeatureEnabled} />
            )
          }
          disableDefaultActions
        />
      </Box>
      <DataTable.Table
        selectable={() => true}
        emptyState={
          <DocumentsDataTableEmptyState
            onUpload={readonly ? undefined : onUpload}
            withFilters={isFiltering}
            isDragging={isDragging}
          />
        }
      />
      <ContextFromPage
        parentRoute={parentRoute}
        folder={folder}
        space={space}
        initialFolder={initialFolder}
      />
    </DataTable.Provider>
  )

  if (readonly) {
    return datatable
  }

  return (
    <DraggableDatatable onDrop={onDrop} multiple={multiple} onDragging={onDragging}>
      {datatable}
    </DraggableDatatable>
  )
}

export default CompanyDocumentsDataTable
